import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Building2, FileText, Calendar as CalendarIcon, UserCheck, ArrowLeft, Video, PlayCircle,
  CheckCircle2, ClipboardList, MessageSquare, Pencil, Save, Trash2, AlertTriangle, Loader2,
  Plus, Users as UsersIcon, UserPlus, UserMinus, Mail,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { PhaseChecklist } from "@/components/PhaseChecklist";
import { ProvidenciasPanel } from "@/pages/shared/Providencias";
import { TiptapEditor } from "@/components/TiptapEditor";
import { api, formatDateBR, formatDateTimeBR, formatCurrencyBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState("");

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Insert phase dialog
  const [insertPhaseOpen, setInsertPhaseOpen] = useState(false);
  const [insertPhaseForm, setInsertPhaseForm] = useState({
    position: 1,
    title: "",
    description: "",
    type: "task",
    requires_legal_review: false,
    requires_director_approval: false,
  });
  const [insertingPhase, setInsertingPhase] = useState(false);

  // New meeting dialog
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: "", agenda: "", scheduled_at: "", duration_minutes: 60, participants: "",
  });

  // New document dialog
  const [docOpen, setDocOpen] = useState(false);
  const [docForm, setDocForm] = useState({
    title: "",
    description: "",
    phase_index: 0,
    file_url: "",
    file_name: "",
    visible_to_client: true,
    publish_now: false,
  });
  const [docFileBusy, setDocFileBusy] = useState(false);

  // Welcome message editor
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [welcomeSaving, setWelcomeSaving] = useState(false);

  // Melhorias 1 e 2 (2026-06-12): exclusão por Diretora.
  // IMPORTANTE: estes hooks DEVEM ficar antes do early return `if (!contract)`
  // — caso contrário a contagem de hooks muda entre renders e quebra com
  // React error #310 (Rendered more hooks than during the previous render).
  const [deletingMeetingId, setDeletingMeetingId] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const refresh = async () => {
    const [{ data: c }, { data: m }, { data: d }, { data: act }] = await Promise.all([
      api.get(`/contracts/${id}`),
      api.get(`/meetings`, { params: { contract_id: id } }),
      api.get(`/documents`, { params: { contract_id: id } }),
      api.get(`/contracts/${id}/activity`),
    ]);
    setContract(c);
    setMeetings(m);
    setDocuments(d);
    setActivity(act);
    setWelcomeMsg(c.welcome_message || "");
  };

  useEffect(() => {
    refresh();
    api.get("/users", { params: { role: ROLES.CONSULTOR } })
      .then(({ data }) => setConsultants(data))
      .catch(() => {});
  }, [id]);

  if (!contract) return <Skeleton className="h-96" />;

  const isSecretariaOrDir = user.role === ROLES.SECRETARIA || user.role === ROLES.DIRETORA;
  const isConsultor = user.role === ROLES.CONSULTOR;
  const isJuridico = user.role === ROLES.JURIDICO;
  const isDiretora = user.role === ROLES.DIRETORA;

  // ---- Melhorias 1 e 2: handlers de exclusão (states já declarados acima do early return) ----
  const deleteMeeting = async (m) => {
    const ok = window.confirm(
      `Tem certeza que deseja excluir a reunião "${m.title}"? `
      + "Essa ação não poderá ser desfeita."
    );
    if (!ok) return;
    setDeletingMeetingId(m.id);
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success("Reunião excluída.");
      // Recarrega só as reuniões
      const { data } = await api.get(`/meetings`, { params: { contract_id: id } });
      setMeetings(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir reunião.");
    } finally {
      setDeletingMeetingId(null);
    }
  };

  const deleteDocument = async (d) => {
    const ok = window.confirm(
      `Tem certeza que deseja excluir o documento "${d.title}"? `
      + "Essa ação não poderá ser desfeita."
    );
    if (!ok) return;
    setDeletingDocId(d.id);
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Documento excluído.");
      const { data } = await api.get(`/documents`, { params: { contract_id: id } });
      setDocuments(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir documento.");
    } finally {
      setDeletingDocId(null);
    }
  };

  const assignConsultant = async () => {
    if (!selectedConsultant) return toast.error("Selecione um consultor.");
    try {
      await api.post(`/contracts/${id}/assign-consultant`, { consultant_id: selectedConsultant });
      toast.success("Consultor designado. Aguardando aprovação da Diretora.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao designar consultor.");
    }
  };

  const approveConsultant = async () => {
    try {
      await api.post(`/contracts/${id}/approve-consultant`);
      toast.success("Designação aprovada. Contrato agora está ATIVO.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  const advancePhase = async (targetIndex) => {
    try {
      const { data } = await api.post(`/contracts/${id}/advance-phase`, { target_index: targetIndex });
      // M20 (2026-06-17): quando a última fase é concluída, o backend
      // finaliza o contrato automaticamente (progress=100, status completed).
      if (data && data.progress_pct === 100 && data.status === "completed") {
        toast.success("Contrato finalizado com sucesso! Última fase concluída — contrato marcado como concluído.");
      } else {
        toast.success("Fase atualizada.");
      }
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao avançar fase.");
    }
  };

  // Melhoria 5 (2026-06-17): permite Diretora/Secretária desfazer avanço
  // de uma fase. Recalcula progress automaticamente no backend.
  const regressPhase = async (phaseIndex) => {
    const state = (contract?.phases_state || []).find(
      (p) => p.phase_index === phaseIndex || p.phase_index === phaseIndex + 1,
    ) || (contract?.phases_state || [])[phaseIndex] || { status: "pending" };
    const targetLabel = state.status === "completed"
      ? "'Em andamento'"
      : "'Não iniciada'";
    const ok = window.confirm(
      `Desfazer o avanço da fase #${phaseIndex + 1}?\n\n`
      + `Status atual: ${state.status}\n`
      + `A fase voltará para o status ${targetLabel} e o percentual de execução do contrato será recalculado.\n\n`
      + "Esta ação será registrada no histórico do contrato.",
    );
    if (!ok) return;
    try {
      await api.post(`/contracts/${id}/regress-phase`, { phase_index: phaseIndex });
      toast.success("Avanço desfeito. Progresso recalculado.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao desfazer avanço.");
    }
  };

  const createMeeting = async () => {
    try {
      const body = {
        contract_id: id,
        title: meetingForm.title,
        agenda: meetingForm.agenda,
        scheduled_at: new Date(meetingForm.scheduled_at).toISOString(),
        duration_minutes: Number(meetingForm.duration_minutes) || 60,
        participants: meetingForm.participants.split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.post("/meetings", body);
      toast.success("Reunião cadastrada. Sala Daily.co gerada.");
      setMeetingOpen(false);
      setMeetingForm({ title: "", agenda: "", scheduled_at: "", duration_minutes: 60, participants: "" });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar reunião.");
    }
  };

  const onDocFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Arquivo excede 15MB.");
      e.target.value = "";
      return;
    }
    setDocFileBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/documents/upload-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocForm((prev) => ({
        ...prev,
        file_url: data.file_url,
        file_name: data.file_name,
      }));
      toast.success(`Arquivo "${data.file_name}" carregado.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro no upload.");
      e.target.value = "";
    } finally {
      setDocFileBusy(false);
    }
  };

  const createDocument = async () => {
    if (!docForm.title.trim()) {
      toast.error("Informe o título do documento.");
      return;
    }
    if (!docForm.file_url) {
      toast.error("Anexe o arquivo do documento antes de salvar.");
      return;
    }
    try {
      await api.post("/documents", {
        contract_id: id,
        title: docForm.title.trim(),
        description: docForm.description,
        phase_index: Number(docForm.phase_index) || 0,
        file_name: docForm.file_name,
        file_url: docForm.file_url,
        visible_to_client: docForm.visible_to_client,
        publish_now: docForm.publish_now && user.role === ROLES.DIRETORA,
      });
      toast.success(
        docForm.publish_now && user.role === ROLES.DIRETORA
          ? "Documento publicado diretamente ao cliente."
          : "Documento criado como Rascunho.",
      );
      setDocOpen(false);
      setDocForm({
        title: "",
        description: "",
        phase_index: 0,
        file_url: "",
        file_name: "",
        visible_to_client: true,
        publish_now: false,
      });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar documento.");
    }
  };

  const submitDoc = async (docId) => {
    try {
      await api.post(`/documents/${docId}/submit-legal`, { comment: "Enviado para revisão." });
      toast.success("Documento enviado ao Controle.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  const saveWelcomeMessage = async () => {
    setWelcomeSaving(true);
    try {
      await api.put(`/contracts/${id}/welcome-message`, { welcome_message: welcomeMsg });
      toast.success("Mensagem 'Comece por aqui' atualizada.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    } finally {
      setWelcomeSaving(false);
    }
  };

  // ---- Delete contract (Diretora only) ----
  const openDeleteDialog = async () => {
    try {
      const { data } = await api.get(`/contracts/${id}/delete-preview`);
      setDeletePreview(data);
      setDeleteConfirmText("");
      setDeleteOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar preview.");
    }
  };

  const confirmDelete = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== "EXCLUIR") {
      toast.error('Digite "EXCLUIR" exatamente para confirmar.');
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/contracts/${id}`);
      toast.success("Contrato excluído com sucesso.");
      setDeleteOpen(false);
      // Navigate back to contract list based on role
      const basePath =
        user.role === ROLES.DIRETORA
          ? "/diretora"
          : user.role === ROLES.SECRETARIA
            ? "/secretaria"
            : user.role === ROLES.CONSULTOR
              ? "/consultor"
              : "/juridico";
      navigate(`${basePath}/contratos`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir.");
    } finally {
      setDeleting(false);
    }
  };

  // ---- Insert new phase into this contract (Diretora) ----
  const openInsertPhase = () => {
    setInsertPhaseForm({
      position: (contract?.product_phases?.length || 0) + 1,
      title: "",
      description: "",
      type: "task",
      requires_legal_review: false,
      requires_director_approval: false,
    });
    setInsertPhaseOpen(true);
  };

  const confirmInsertPhase = async () => {
    if (!insertPhaseForm.title.trim()) {
      toast.error("Informe o título da nova fase.");
      return;
    }
    setInsertingPhase(true);
    try {
      await api.post(`/contracts/${id}/insert-phase`, {
        position: parseInt(insertPhaseForm.position, 10) || 1,
        title: insertPhaseForm.title.trim(),
        description: insertPhaseForm.description.trim(),
        type: insertPhaseForm.type,
        requires_legal_review: insertPhaseForm.requires_legal_review,
        requires_director_approval: insertPhaseForm.requires_director_approval,
      });
      toast.success("Fase inserida com sucesso.");
      setInsertPhaseOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao inserir fase.");
    } finally {
      setInsertingPhase(false);
    }
  };

  const backLink = isConsultor ? "/consultor/contratos" :
    user.role === ROLES.DIRETORA ? "/diretora/contratos" :
    "/secretaria/contratos";

  return (
    <div className="space-y-6">
      <Link to={backLink} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar para contratos
      </Link>

      <Card className="card-soft">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-6 justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-slate-500" />
                <span className="text-lg font-semibold text-slate-900">{contract.client_name}</span>
                <StatusBadge status={contract.status} />
              </div>
              <div className="serif text-2xl text-slate-900">{contract.product_name}</div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                <div>Início: <span className="font-medium">{formatDateBR(contract.start_date)}</span></div>
                <div>Término: <span className="font-medium">{formatDateBR(contract.end_date)}</span></div>
                {contract.consultant_name && <div>Consultor: <span className="font-medium">{contract.consultant_name}</span></div>}
                {contract.contract_value && <div>Valor: <span className="font-medium">{formatCurrencyBR(contract.contract_value)}</span></div>}
              </div>
              {contract.notes && (
                <p className="mt-3 text-sm text-slate-600 max-w-xl">{contract.notes}</p>
              )}
            </div>
            <div className="lg:w-64">
              <div className="text-xs uppercase tracking-wide text-slate-500">Avanço do contrato</div>
              <div className="mt-1 flex items-end gap-2">
                <span className="text-3xl font-semibold text-slate-900">{contract.progress_pct}%</span>
                <span className="text-xs text-slate-500 pb-1">
                  fase {contract.current_phase_index + 1} de {contract.product_phases?.length}
                </span>
              </div>
              <Progress value={contract.progress_pct} className="mt-2 h-2" />
              {(user.role === ROLES.DIRETORA || user.role === ROLES.SECRETARIA) && (
                <Link
                  to={`${user.role === ROLES.DIRETORA ? "/diretora" : "/secretaria"}/contratos/${id}/prestacao`}
                >
                  <Button
                    size="sm"
                    className="mt-4 w-full bg-gold text-ink hover:bg-gold-deep hover:text-white"
                    data-testid="open-prestacao-button"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5" />
                    Prestação de Contas
                  </Button>
                </Link>
              )}
              {user.role === ROLES.DIRETORA && (
                <Button
                  onClick={openDeleteDialog}
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="delete-contract-button"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Excluir contrato
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Designation flow */}
      {contract.status === "pending_assignment" && isSecretariaOrDir && (
        <Card className="card-soft border-amber-200 bg-amber-50/50">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <div className="font-medium text-slate-900">Designar consultor responsável</div>
                <div className="text-sm text-slate-600">A designação ficará pendente até a aprovação da Diretora.</div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
                  <SelectTrigger className="min-w-[240px]" data-testid="assign-consultant-select">
                    <SelectValue placeholder="Selecione consultor…" />
                  </SelectTrigger>
                  <SelectContent>
                    {consultants.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={assignConsultant} className="bg-midnight hover:bg-ink text-white" data-testid="assign-consultant-button">
                  Designar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {contract.status === "pending_director_approval" && user.role === ROLES.DIRETORA && (
        <Card className="card-soft border-amber-200 bg-amber-50/50">
          <CardContent className="p-5 flex justify-between items-center gap-3">
            <div>
              <div className="font-medium text-slate-900">Aprovar designação do consultor</div>
              <div className="text-sm text-slate-600">Consultor proposto: <strong>{contract.consultant_name}</strong></div>
            </div>
            <Button onClick={approveConsultant} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="approve-consultant-button">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Aprovar designação
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="welcome" className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          {(user.role === ROLES.SECRETARIA || user.role === ROLES.DIRETORA || user.role === ROLES.CONSULTOR) && (
            <TabsTrigger value="welcome" data-testid="tab-welcome"><Pencil className="w-4 h-4 mr-2" /> "Comece por aqui"</TabsTrigger>
          )}
          <TabsTrigger value="phases" data-testid="tab-phases"><ClipboardList className="w-4 h-4 mr-2" /> Fases</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="tab-meetings"><CalendarIcon className="w-4 h-4 mr-2" /> Reuniões</TabsTrigger>
          <TabsTrigger value="videos" data-testid="tab-videos"><Video className="w-4 h-4 mr-2" /> Biblioteca de Vídeos</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-4 h-4 mr-2" /> Documentos</TabsTrigger>
          {(user.role === ROLES.SECRETARIA || user.role === ROLES.DIRETORA) && (
            <TabsTrigger value="providencias" data-testid="tab-providencias"><ClipboardList className="w-4 h-4 mr-2" /> Providências</TabsTrigger>
          )}
          {/* M16 (2026-06-17): aba Usuários vinculados ao contrato */}
          {(user.role === ROLES.SECRETARIA || user.role === ROLES.DIRETORA || user.role === ROLES.CONSULTOR) && (
            <TabsTrigger value="users" data-testid="tab-users"><UsersIcon className="w-4 h-4 mr-2" /> Usuários</TabsTrigger>
          )}
          <TabsTrigger value="activity" data-testid="tab-activity"><MessageSquare className="w-4 h-4 mr-2" /> Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="phases">
          <Card className="card-soft">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Fluxo do produto contratado</CardTitle>
              {user.role === ROLES.DIRETORA && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openInsertPhase}
                  data-testid="insert-contract-phase-button"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Inserir nova fase
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <PhaseChecklist
                phases={contract.product_phases || []}
                phasesState={contract.phases_state || []}
                onAdvance={advancePhase}
                onRegress={regressPhase}
                canAdvance={isConsultor || isSecretariaOrDir}
                canRegress={isSecretariaOrDir}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meetings">
          <Card className="card-soft">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle className="text-base">Reuniões do contrato</CardTitle>
              {(isSecretariaOrDir || isConsultor) && (
                <Dialog open={meetingOpen} onOpenChange={setMeetingOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-midnight hover:bg-ink text-white" data-testid="new-meeting-button">
                      <Video className="w-4 h-4 mr-2" /> Nova reunião
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Cadastrar reunião</DialogTitle>
                      <DialogDescription>Uma sala Daily.co será gerada automaticamente.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label>Título *</Label>
                        <Input value={meetingForm.title}
                          onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                          data-testid="meeting-title-input" />
                      </div>
                      <div>
                        <Label>Pauta</Label>
                        <Textarea value={meetingForm.agenda}
                          onChange={(e) => setMeetingForm({ ...meetingForm, agenda: e.target.value })}
                          rows={3} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Data e hora *</Label>
                          <Input type="datetime-local" value={meetingForm.scheduled_at}
                            onChange={(e) => setMeetingForm({ ...meetingForm, scheduled_at: e.target.value })} />
                        </div>
                        <div>
                          <Label>Duração (min)</Label>
                          <Input type="number" value={meetingForm.duration_minutes}
                            onChange={(e) => setMeetingForm({ ...meetingForm, duration_minutes: e.target.value })} />
                        </div>
                      </div>
                      <div>
                        <Label>Participantes (separados por vírgula)</Label>
                        <Input value={meetingForm.participants}
                          onChange={(e) => setMeetingForm({ ...meetingForm, participants: e.target.value })} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setMeetingOpen(false)}>Cancelar</Button>
                      <Button onClick={createMeeting} className="bg-midnight hover:bg-ink text-white" data-testid="meeting-submit-button">
                        Criar reunião
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {meetings.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Nenhuma reunião cadastrada.</div>
              ) : (
                <div className="space-y-3">
                  {meetings.map((m) => (
                    <div
                      key={m.id}
                      className="relative block p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`meeting-card-${m.id}`}
                    >
                      <Link
                        to={`/secretaria/reunioes/${m.id}`}
                        className="block pr-12"
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Video className="w-4 h-4 text-slate-500" />
                              <span className="font-medium text-slate-900">{m.title}</span>
                              <StatusBadge status={m.status} />
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {formatDateTimeBR(m.scheduled_at)} · {m.duration_minutes} min
                            </div>
                            {m.agenda && (
                              <div className="text-xs text-slate-600 mt-1 line-clamp-1">{m.agenda}</div>
                            )}
                          </div>
                          {m.room_placeholder && (
                            <span className="text-xs text-amber-700">Sala em modo demo</span>
                          )}
                        </div>
                      </Link>
                      {isDiretora && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteMeeting(m);
                          }}
                          disabled={deletingMeetingId === m.id}
                          className="absolute top-3 right-3 h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                          aria-label="Excluir reunião"
                          title="Excluir reunião"
                          data-testid={`delete-meeting-${m.id}`}
                        >
                          {deletingMeetingId === m.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="videos">
          <VideoLibraryTab
            contractId={id}
            basePath={
              user.role === ROLES.DIRETORA ? "/diretora" :
              user.role === ROLES.SECRETARIA ? "/secretaria" :
              user.role === ROLES.CONSULTOR ? "/consultor" :
              user.role === ROLES.JURIDICO ? "/juridico" : "/portal"
            }
          />
        </TabsContent>

        <TabsContent value="documents">
          <Card className="card-soft">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle className="text-base">Documentos & entregáveis</CardTitle>
              {(isSecretariaOrDir || isConsultor || isJuridico) && (
                <Dialog open={docOpen} onOpenChange={setDocOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-midnight hover:bg-ink text-white" data-testid="new-document-button">
                      <FileText className="w-4 h-4 mr-2" /> Novo documento
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Novo documento</DialogTitle>
                      <DialogDescription>
                        {user.role === ROLES.DIRETORA
                          ? "Diretora pode publicar diretamente ou seguir o fluxo de revisão."
                          : user.role === ROLES.JURIDICO
                            ? "O documento será enviado para aprovação da Diretora antes de ser publicado ao cliente."
                            : "O documento será criado como Rascunho. Envie ao Controle/Jurídico quando estiver pronto."}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label>Título *</Label>
                        <Input value={docForm.title}
                          onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                          data-testid="doc-title-input" />
                      </div>
                      <div>
                        <Label>Descrição</Label>
                        <Textarea value={docForm.description}
                          onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
                          rows={3} />
                      </div>
                      <div>
                        <Label>Arquivo *</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="file"
                            id="doc-file-input"
                            onChange={onDocFilePick}
                            disabled={docFileBusy}
                            className="block w-full text-sm border border-slate-300 rounded-md p-2 file:mr-2 file:px-3 file:py-1 file:text-xs file:rounded file:border-0 file:bg-midnight file:text-white"
                            data-testid="doc-file-input"
                          />
                        </div>
                        {docFileBusy && (
                          <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> Enviando arquivo…
                          </p>
                        )}
                        {docForm.file_url && (
                          <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> {docForm.file_name}
                          </p>
                        )}
                        <p className="text-[11px] text-slate-500 mt-1">
                          PDF, DOCX, XLSX, ZIP, imagens (até 15MB)
                        </p>
                      </div>
                      <div>
                        <Label>Fase associada</Label>
                        <Select
                          value={String(docForm.phase_index)}
                          onValueChange={(v) => setDocForm({ ...docForm, phase_index: Number(v) })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(contract.product_phases || []).map((p, i) => (
                              <SelectItem key={i} value={String(i)}>Fase {i + 1} — {p.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={docForm.visible_to_client}
                          onChange={(e) => setDocForm({ ...docForm, visible_to_client: e.target.checked })}
                          className="rounded border-slate-300"
                          data-testid="doc-visible-checkbox"
                        />
                        Visível para o cliente (após publicação)
                      </label>
                      {user.role === ROLES.DIRETORA && (
                        <label className="flex items-center gap-2 text-sm text-slate-700 p-2 bg-amber-50 border border-amber-200 rounded-md">
                          <input
                            type="checkbox"
                            checked={docForm.publish_now}
                            onChange={(e) => setDocForm({ ...docForm, publish_now: e.target.checked })}
                            className="rounded border-slate-300"
                            data-testid="doc-publish-now-checkbox"
                          />
                          <strong>Publicar imediatamente</strong> (pula o fluxo de revisão)
                        </label>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDocOpen(false)}>Cancelar</Button>
                      <Button
                        onClick={createDocument}
                        disabled={docFileBusy || !docForm.file_url || !docForm.title.trim()}
                        className="bg-midnight hover:bg-ink text-white"
                        data-testid="doc-submit-button"
                      >
                        {docForm.publish_now && user.role === ROLES.DIRETORA ? "Publicar agora" : "Criar documento"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Nenhum documento.</div>
              ) : (
                <div className="space-y-3">
                  {documents.map((d) => (
                    <div key={d.id} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <FileText className="w-4 h-4 text-slate-500" />
                            <span className="font-medium text-slate-900">{d.title}</span>
                            <StatusBadge status={d.status} />
                            <span className="text-xs text-slate-500">v{d.current_version}</span>
                          </div>
                          {d.description && (
                            <div className="text-xs text-slate-600 mt-1">{d.description}</div>
                          )}
                          <div className="text-xs text-slate-500 mt-1">
                            Criado por {d.created_by_name} em {formatDateTimeBR(d.created_at)}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {(d.status === "draft" || d.status === "legal_changes_requested" || d.status === "director_changes_requested") &&
                            (isConsultor || isSecretariaOrDir) && (
                            <Button size="sm" variant="outline" onClick={() => submitDoc(d.id)} data-testid={`submit-legal-${d.id}`}>
                              {isConsultor ? "Enviar ao Controle" : "Enviar ao Jurídico"}
                            </Button>
                          )}
                          <Link to={`/documentos/${d.id}`}>
                            <Button size="sm" variant="outline">Detalhes</Button>
                          </Link>
                          {isDiretora && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteDocument(d)}
                              disabled={deletingDocId === d.id}
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                              title="Excluir documento (Diretora)"
                              data-testid={`delete-document-${d.id}`}
                            >
                              {deletingDocId === d.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="providencias">
          <ProvidenciasPanel contractId={id} />
        </TabsContent>

        <TabsContent value="welcome">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Pencil className="w-4 h-4 text-midnight" /> Comece por aqui — visível ao cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900">
                Este texto aparece no <strong>topo do Portal do Cliente</strong>, antes da seção de fases.
                Use o editor para destacar avisos, inserir links clicáveis (ex.: grupo do WhatsApp) e formatar listas.
              </div>
              <TiptapEditor
                value={welcomeMsg}
                onChange={setWelcomeMsg}
                placeholder="Ex.: Olá, seja bem-vindo! Link do grupo WhatsApp: ..."
              />
              <div className="flex justify-end">
                <Button onClick={saveWelcomeMessage} disabled={welcomeSaving}
                  className="bg-midnight hover:bg-ink text-white" data-testid="save-welcome-button">
                  <Save className="w-4 h-4 mr-2" />
                  {welcomeSaving ? "Salvando..." : "Salvar mensagem"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* M16 (2026-06-17): aba Usuários vinculados ao contrato */}
        <TabsContent value="users">
          <UsersTab
            contractId={id}
            contract={contract}
            canManage={isSecretariaOrDir}
            onChanged={refresh}
          />
        </TabsContent>

        <TabsContent value="activity">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base">Histórico de atividades</CardTitle>
            </CardHeader>
            <CardContent>
              {activity.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Sem atividades registradas.</div>
              ) : (
                <ul className="space-y-3">
                  {activity.map((a) => (
                    <li key={a.id} className="flex gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-midnight mt-2 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-slate-900">{a.details}</div>
                        <div className="text-xs text-slate-500">
                          {a.actor_name || "Sistema"} · {formatDateTimeBR(a.created_at)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Insert new phase dialog (Diretora) */}
      <Dialog open={insertPhaseOpen} onOpenChange={(v) => !insertingPhase && setInsertPhaseOpen(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Inserir nova fase no contrato</DialogTitle>
            <DialogDescription>
              Este contrato tem <strong>{contract?.product_phases?.length || 0}</strong> fases.
              Indique em qual posição inserir a nova fase. As demais serão renumeradas automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Inserir na posição número *</Label>
              <Input
                type="number"
                min={1}
                max={(contract?.product_phases?.length || 0) + 1}
                value={insertPhaseForm.position}
                onChange={(e) => setInsertPhaseForm({ ...insertPhaseForm, position: e.target.value })}
                data-testid="contract-insert-position-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Use <strong>{(contract?.product_phases?.length || 0) + 1}</strong> para inserir ao final.
              </p>
            </div>
            <div>
              <Label>Título da fase *</Label>
              <Input
                value={insertPhaseForm.title}
                onChange={(e) => setInsertPhaseForm({ ...insertPhaseForm, title: e.target.value })}
                placeholder="Ex.: Revisão extraordinária"
                data-testid="contract-insert-title-input"
              />
            </div>
            <div>
              <Label>Descrição (opcional)</Label>
              <Textarea
                rows={2}
                value={insertPhaseForm.description}
                onChange={(e) => setInsertPhaseForm({ ...insertPhaseForm, description: e.target.value })}
                placeholder="O que precisa ser feito nesta fase"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={insertPhaseForm.requires_legal_review}
                  onChange={(e) => setInsertPhaseForm({ ...insertPhaseForm, requires_legal_review: e.target.checked })}
                  className="rounded border-slate-300"
                />
                Requer revisão jurídica
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={insertPhaseForm.requires_director_approval}
                  onChange={(e) => setInsertPhaseForm({ ...insertPhaseForm, requires_director_approval: e.target.checked })}
                  className="rounded border-slate-300"
                />
                Requer aprovação da Diretora
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsertPhaseOpen(false)} disabled={insertingPhase}>
              Cancelar
            </Button>
            <Button
              onClick={confirmInsertPhase}
              disabled={insertingPhase}
              className="bg-midnight hover:bg-ink text-white"
              data-testid="contract-insert-confirm"
            >
              {insertingPhase ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Inserir fase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete contract confirmation dialog (Diretora only) */}
      <Dialog open={deleteOpen} onOpenChange={(v) => !deleting && setDeleteOpen(v)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Excluir contrato permanentemente?
            </DialogTitle>
            <DialogDescription>
              Esta ação <strong>não pode ser desfeita</strong>. Todos os dados
              relacionados serão excluídos em cascata.
            </DialogDescription>
          </DialogHeader>

          {deletePreview && (
            <div className="space-y-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm font-semibold text-red-900 mb-2">
                  Será apagado:
                </div>
                <ul className="text-sm text-slate-800 space-y-1">
                  <li>• <strong>{deletePreview.counts.meetings}</strong> reuniões (incluindo gravações)</li>
                  <li>• <strong>{deletePreview.counts.documents}</strong> documentos</li>
                  <li>• <strong>{deletePreview.counts.providencias}</strong> providências</li>
                  <li>• <strong>{deletePreview.counts.directives}</strong> diretivas</li>
                  <li>• <strong>{deletePreview.counts.reports}</strong> relatórios mensais</li>
                  <li>• <strong>{deletePreview.counts.ratings}</strong> avaliações</li>
                  <li>• <strong>{deletePreview.counts.activity_logs}</strong> entradas do histórico</li>
                </ul>
              </div>

              {deletePreview.orphan_client_users?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <div className="text-sm font-semibold text-amber-900 mb-2 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Atenção: usuários CLIENTE vinculados
                  </div>
                  <p className="text-xs text-slate-700 mb-1">
                    Os seguintes logins de CLIENTE ficarão <strong>sem
                    acesso</strong> (desvinculados) após a exclusão. Será
                    necessário reatribuí-los manualmente:
                  </p>
                  <ul className="text-xs text-slate-800 ml-3 list-disc">
                    {deletePreview.orphan_client_users.map((u) => (
                      <li key={u.id}>{u.name} ({u.email})</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <Label className="text-sm">
                  Para confirmar, digite{" "}
                  <code className="px-1 bg-slate-100 rounded">EXCLUIR</code>:
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="EXCLUIR"
                  className="mt-1 border-red-300 focus-visible:ring-red-400"
                  data-testid="delete-confirm-input"
                  disabled={deleting}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting || deleteConfirmText.trim().toUpperCase() !== "EXCLUIR"}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="delete-confirm-button"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Excluir definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------------------------------------------------------------------
// VideoLibraryTab — Biblioteca de gravações (Vimeo + Drive)
// M17 (2026-06-17): consolidada — mostra tanto Vimeo (manual) quanto
// gravações automáticas do Google Drive/Meet.
// ---------------------------------------------------------------------------
function VideoLibraryTab({ contractId, basePath }) {
  const [videos, setVideos] = useState(null);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    api
      .get(`/meetings/contract/${contractId}/library`)
      .then(({ data }) => {
        setVideos(data);
        if (data?.length) setActiveId(data[0].id);
      })
      .catch(() => setVideos([]));
  }, [contractId]);

  if (videos === null) return <Skeleton className="h-64" />;

  if (videos.length === 0) {
    return (
      <Card className="card-soft" data-testid="video-library-empty">
        <CardContent className="py-12 text-center">
          <Video className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="text-sm font-medium text-slate-700">
            Nenhuma gravação disponível ainda
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Gravações aparecem aqui automaticamente após reuniões do Google Meet
            ou quando um link do Vimeo é inserido manualmente nas reuniões.
          </p>
        </CardContent>
      </Card>
    );
  }

  const active = videos.find((v) => v.id === activeId) || videos[0];
  const activeSrc = active?.origin === "drive"
    ? active?.drive_recording_embed_url
    : active?.vimeo_video_id
      ? `https://player.vimeo.com/video/${active.vimeo_video_id}${active.vimeo_hash ? `?h=${active.vimeo_hash}` : ""}`
      : null;

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6" data-testid="video-library">
      {/* Main player */}
      <Card className="card-soft overflow-hidden">
        <CardContent className="p-0">
          <div
            className="relative w-full bg-slate-900"
            style={{ paddingBottom: "56.25%" }}
            data-testid="video-library-player"
          >
            {activeSrc ? (
              <iframe
                key={active.id}
                src={activeSrc}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={`Gravação: ${active.title}`}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                Gravação sem player disponível.
              </div>
            )}
          </div>
          <div className="p-4 space-y-1">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">
                  {active.title}
                </h3>
                <div className="mt-1">
                  <span
                    className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded border ${
                      active.origin === "drive"
                        ? "bg-blue-50 text-blue-800 border-blue-200"
                        : "bg-emerald-50 text-emerald-800 border-emerald-200"
                    }`}
                    data-testid={`video-origin-${active.origin}`}
                  >
                    {active.origin_label || (active.origin === "drive" ? "Automática (Drive)" : "Manual (Vimeo)")}
                  </span>
                </div>
              </div>
              <Link
                to={`${basePath}/reunioes/${active.id}`}
                className="text-xs text-midnight underline whitespace-nowrap"
              >
                Abrir reunião →
              </Link>
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
              <span>
                <CalendarIcon className="inline w-3 h-3 mr-1" />
                {formatDateTimeBR(active.scheduled_at)}
              </span>
              <span>{active.duration_minutes} min</span>
              {active.minutes_published && (
                <span className="text-emerald-700">
                  <CheckCircle2 className="inline w-3 h-3 mr-1" /> Ata publicada
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Playlist */}
      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-sm">
            {videos.length} gravação{videos.length === 1 ? "" : "ões"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 max-h-[500px] overflow-y-auto scroll-thin">
          <ul>
            {videos.map((v) => (
              <li
                key={v.id}
                onClick={() => setActiveId(v.id)}
                className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                  active?.id === v.id ? "bg-blue-50/60 border-l-4 border-l-midnight" : ""
                }`}
                data-testid={`video-item-${v.id}`}
              >
                <div className="flex items-start gap-2">
                  <Video className={`w-4 h-4 mt-0.5 shrink-0 ${v.origin === "drive" ? "text-blue-700" : "text-emerald-700"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {v.title}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <span>{formatDateTimeBR(v.scheduled_at)}</span>
                      <span className={`text-[10px] px-1.5 rounded ${
                        v.origin === "drive" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"
                      }`}>
                        {v.origin === "drive" ? "Drive" : "Vimeo"}
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// M16 (2026-06-17): UsersTab — Usuários vinculados ao contrato
// Mostra: consultor principal, consultores adicionais, e clientes vinculados.
// Diretora/Secretária podem adicionar/remover consultores adicionais.
// ---------------------------------------------------------------------------
function UsersTab({ contractId, contract, canManage, onChanged }) {
  const [users, setUsers] = useState(null);
  const [availableConsultants, setAvailableConsultants] = useState([]);
  const [pickedConsultant, setPickedConsultant] = useState("");
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = () => {
    api.get(`/contracts/${contractId}/users`).then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  };

  useEffect(() => {
    load();
    if (canManage) {
      api.get("/users?role=CONSULTOR").then(({ data }) => setAvailableConsultants(data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, canManage]);

  const alreadyLinked = new Set();
  (users || []).forEach((u) => {
    if (u.role === "CONSULTOR") alreadyLinked.add(u.id);
  });
  const candidateConsultants = availableConsultants.filter((c) => !alreadyLinked.has(c.id));

  const doAdd = async () => {
    if (!pickedConsultant) return toast.error("Selecione um consultor.");
    setAdding(true);
    try {
      await api.post(`/contracts/${contractId}/add-consultant`, { user_id: pickedConsultant });
      toast.success("Consultor adicional vinculado.");
      setPickedConsultant("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao adicionar consultor.");
    } finally {
      setAdding(false);
    }
  };

  const doRemove = async (u) => {
    if (u.vinculo_type !== "consultor_adicional") return;
    if (!window.confirm(`Remover ${u.name} como consultor adicional deste contrato?\n\nO usuário continuará existindo no sistema — apenas o vínculo com este contrato será desfeito.`)) return;
    setRemovingId(u.id);
    try {
      await api.delete(`/contracts/${contractId}/remove-consultant/${u.id}`);
      toast.success("Consultor adicional removido.");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover consultor.");
    } finally {
      setRemovingId(null);
    }
  };

  if (users === null) return <Skeleton className="h-64" />;

  const groupIcon = (type) => {
    if (type === "consultor_principal") return <UserCheck className="w-4 h-4 text-emerald-700" />;
    if (type === "consultor_adicional") return <UsersIcon className="w-4 h-4 text-sky-700" />;
    if (type === "cliente") return <Building2 className="w-4 h-4 text-amber-700" />;
    return <UsersIcon className="w-4 h-4 text-slate-500" />;
  };
  const groupBadge = (type) => {
    if (type === "consultor_principal") return { label: "Consultor principal", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" };
    if (type === "consultor_adicional") return { label: "Consultor adicional", cls: "bg-sky-50 text-sky-800 border-sky-200" };
    if (type === "cliente") return { label: "Cliente", cls: "bg-amber-50 text-amber-800 border-amber-200" };
    return { label: type, cls: "bg-slate-100 text-slate-700 border-slate-300" };
  };

  return (
    <div className="space-y-6" data-testid="contract-users-tab">
      {canManage && (
        <Card className="card-soft">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-midnight" />
              Adicionar consultor adicional
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 items-end flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <Label>Consultor</Label>
                <Select value={pickedConsultant} onValueChange={setPickedConsultant}>
                  <SelectTrigger className="mt-1" data-testid="pick-additional-consultant">
                    <SelectValue placeholder="Selecione um consultor…" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidateConsultants.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-500">
                        Nenhum consultor disponível para adicionar.
                      </div>
                    ) : (
                      candidateConsultants.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} · {c.email}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={doAdd}
                disabled={adding || !pickedConsultant}
                className="bg-midnight hover:bg-ink text-white"
                data-testid="add-additional-consultant-btn"
              >
                {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Vincular
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Consultores adicionais podem visualizar e editar o contrato,
              mas o principal continua sendo o responsável formal.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-midnight" />
            Usuários vinculados ao contrato ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              Nenhum usuário vinculado a este contrato ainda.
            </div>
          ) : (
            <ul>
              {users.map((u) => {
                const g = groupBadge(u.vinculo_type);
                return (
                  <li
                    key={`${u.id}-${u.vinculo_type}`}
                    className="flex items-center justify-between gap-3 p-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
                    data-testid={`contract-user-${u.id}-${u.vinculo_type}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {groupIcon(u.vinculo_type)}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                          {u.name}
                          {!u.active && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                              Inativo
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {u.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-[11px] px-2 py-0.5 rounded border ${g.cls}`}>
                        {g.label}
                      </span>
                      {canManage && u.vinculo_type === "consultor_adicional" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => doRemove(u)}
                          disabled={removingId === u.id}
                          data-testid={`remove-additional-consultant-${u.id}`}
                        >
                          {removingId === u.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <UserMinus className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
